import React, { useState, useEffect, useCallback } from 'react';
import { TreePine, Check, Search, Users, AlertTriangle, Eye, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHF = (n) => new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(n || 0);

export const FriendshipTreeSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [qualifyingText, setQualifyingText] = useState('');
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingPreview, setBillingPreview] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [activating, setActivating] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/friendship-tree`, authHeaders());
        setSettings(res.data);
        setQualifyingText((res.data.qualifying_names || []).join(', '));
      } catch (e) {
        console.error('Error loading friendship-tree settings:', e);
      }
    })();
  }, []);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setGen = (key, i, v) => setSettings((s) => {
    const arr = [...(s[key] || [2, 1, 0.5])];
    arr[i] = v;
    return { ...s, [key]: arr };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const dg = (settings.deeper_gen_discounts || []).map((x) => Number(x) || 0);
      const dgp = (settings.deeper_gen_pcts || []).map((x) => Number(x) || 0);
      const qn = qualifyingText.split(',').map((s) => s.trim()).filter(Boolean);
      await axios.put(`${API}/settings/friendship-tree`, {
        enabled: !!settings.enabled,
        discount_mode: settings.discount_mode || 'percent',
        base_price: Number(settings.base_price) || 75,
        min_price: Number(settings.min_price) || 65,
        first_direct_discount: Number(settings.first_direct_discount) || 0,
        additional_direct_discount: Number(settings.additional_direct_discount) || 0,
        deeper_gen_discounts: dg,
        max_total_discount: Number(settings.max_total_discount) || 0,
        first_direct_pct: Number(settings.first_direct_pct) || 0,
        additional_direct_pct: Number(settings.additional_direct_pct) || 0,
        deeper_gen_pcts: dgp,
        max_total_pct: Number(settings.max_total_pct) || 0,
        active_within_days: Number(settings.active_within_days) || 60,
        base_service_id: settings.base_service_id || null,
        qualifying_names: qn,
        max_generations: Number(settings.max_generations) || 6,
      }, authHeaders());
      setSettings((s) => ({ ...s, qualifying_names: qn }));
      toast.success('Freundschaftsbaum-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const openBillingPreview = async () => {
    setBillingDialogOpen(true);
    setLoadingBilling(true);
    setBillingPreview(null);
    try {
      const res = await axios.get(`${API}/settings/friendship-tree/billing-preview`, authHeaders());
      setBillingPreview(res.data);
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
    } finally {
      setLoadingBilling(false);
    }
  };

  const setBilling = async (on) => {
    setActivating(true);
    try {
      const body = { apply_to_billing: on };
      if (on) body.confirm = 'FREUNDESKREIS-ABRECHNUNG';
      const res = await axios.put(`${API}/settings/friendship-tree`, body, authHeaders());
      setSettings((s) => ({ ...s, apply_to_billing: res.data.apply_to_billing }));
      toast.success(on ? 'Netzwerk-Rabatt ist jetzt abrechnungswirksam' : 'Abrechnungswirkung deaktiviert');
      setBillingDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Umschalten');
    } finally {
      setActivating(false);
    }
  };

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/customers`, { ...authHeaders(), params: { search: q.trim() } });
      setResults((res.data || []).slice(0, 8));
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 350);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const loadPreview = async (customerId, name) => {
    setLoadingPreview(true);
    setResults([]);
    setQuery(name || '');
    try {
      const res = await axios.get(`${API}/settings/friendship-tree/customer/${customerId}`, authHeaders());
      setPreview(res.data);
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  if (!settings) return null;
  const isPercent = (settings.discount_mode || 'percent') === 'percent';
  const dg = settings.deeper_gen_discounts || [2, 1, 0.5];
  const dgp = settings.deeper_gen_pcts || [2, 1, 0.5];
  const capLabel = isPercent ? `−${settings.max_total_pct}%` : `−${CHF(settings.max_total_discount)}`;

  return (
    <>
    <Card className="border-green-200" data-testid="friendship-tree-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TreePine className="text-green-600" />
              Freundschaftsbaum – Freundeskreis-Preise
              <Badge className={`text-[10px] ${settings.apply_to_billing ? 'bg-green-600' : 'bg-gray-400'}`}>
                {settings.apply_to_billing ? 'Abrechnung aktiv' : 'Nur Anzeige'}
              </Badge>
            </CardTitle>
            <CardDescription>
              Der Trainingspreis sinkt mit dem eigenen Empfehlungs-Netzwerk. Der berechnete
              Preis wird dem Kunden (App/Portal) und dir angezeigt — die <strong>Abrechnung
              bleibt unverändert</strong>, bis du „Auf Abrechnung anwenden" aktivierst.
            </CardDescription>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setField('enabled', !!v)}
            data-testid="friendship-tree-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Basispreis (CHF)</Label>
            <Input data-testid="ft-base-price" type="number" step="0.5" min="0"
              value={settings.base_price ?? 75}
              onChange={(e) => setField('base_price', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Mindestpreis (CHF)</Label>
            <Input data-testid="ft-min-price" type="number" step="0.5" min="0"
              value={settings.min_price ?? 65}
              onChange={(e) => setField('min_price', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>„Aktiv" = trainiert in ... Tagen</Label>
            <Input data-testid="ft-active-days" type="number" min="1"
              value={settings.active_within_days ?? 60}
              onChange={(e) => setField('active_within_days', e.target.value)} />
          </div>
        </div>

        {/* Rabatt-Modus */}
        <div className="space-y-2">
          <Label>Rabatt-Modus</Label>
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="ft-mode-toggle">
            <button type="button" onClick={() => setField('discount_mode', 'percent')}
              className={`px-4 py-1.5 text-sm font-medium ${isPercent ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}
              data-testid="ft-mode-percent">Prozent (%)</button>
            <button type="button" onClick={() => setField('discount_mode', 'chf')}
              className={`px-4 py-1.5 text-sm font-medium ${!isPercent ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}
              data-testid="ft-mode-chf">Franken (CHF)</button>
          </div>
          <p className="text-xs text-gray-500">
            {isPercent
              ? 'Rabatt als % vom jeweiligen Abo-/Dienstleistungspreis — skaliert fair über 75/45 etc.'
              : 'Rabatt als fester Franken-Betrag, unabhängig vom Preis.'}
          </p>
        </div>

        {/* Rabattwerte (modus-abhängig) */}
        {isPercent ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Gesamt-Deckel Rabatt (%)</Label>
                <Input data-testid="ft-max-pct" type="number" step="0.5" min="0"
                  value={settings.max_total_pct ?? 12}
                  onChange={(e) => setField('max_total_pct', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>1. direkte Empfehlung (−%)</Label>
                <Input data-testid="ft-first-direct-pct" type="number" step="0.5" min="0"
                  value={settings.first_direct_pct ?? 6}
                  onChange={(e) => setField('first_direct_pct', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Jede weitere direkte (−%)</Label>
                <Input data-testid="ft-additional-direct-pct" type="number" step="0.5" min="0"
                  value={settings.additional_direct_pct ?? 2}
                  onChange={(e) => setField('additional_direct_pct', e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rabatt tiefere Generationen (je aktivem Mitglied, −%)</Label>
              <div className="grid grid-cols-3 gap-3 max-w-md">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-1">
                    <p className="text-xs text-gray-500">Gen {i + 2}</p>
                    <Input data-testid={`ft-gen-pct-${i + 2}`} type="number" step="0.5" min="0"
                      value={dgp[i] ?? 0}
                      onChange={(e) => setGen('deeper_gen_pcts', i, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Gesamt-Deckel Rabatt (CHF)</Label>
                <Input data-testid="ft-max-discount" type="number" step="0.5" min="0"
                  value={settings.max_total_discount ?? 10}
                  onChange={(e) => setField('max_total_discount', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>1. direkte Empfehlung (−CHF)</Label>
                <Input data-testid="ft-first-direct" type="number" step="0.5" min="0"
                  value={settings.first_direct_discount ?? 5}
                  onChange={(e) => setField('first_direct_discount', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Jede weitere direkte (−CHF)</Label>
                <Input data-testid="ft-additional-direct" type="number" step="0.5" min="0"
                  value={settings.additional_direct_discount ?? 1.5}
                  onChange={(e) => setField('additional_direct_discount', e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rabatt tiefere Generationen (je aktivem Mitglied, −CHF)</Label>
              <div className="grid grid-cols-3 gap-3 max-w-md">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-1">
                    <p className="text-xs text-gray-500">Gen {i + 2}</p>
                    <Input data-testid={`ft-gen-${i + 2}`} type="number" step="0.5" min="0"
                      value={dg[i] ?? 0}
                      onChange={(e) => setGen('deeper_gen_discounts', i, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="space-y-1">
          <Label>Basis-Service-ID (optional – für Individualpreis-Schutz)</Label>
          <Input data-testid="ft-base-service" type="text" placeholder="Service-ID des Standard-Trainings"
            value={settings.base_service_id || ''}
            onChange={(e) => setField('base_service_id', e.target.value)} />
          <p className="text-xs text-gray-500">
            Wenn gesetzt und der Kunde einen individuellen Preis für diesen Service hat, gilt
            Effektivpreis = max(individueller Preis, Netzwerk-Preis).
          </p>
        </div>

        <div className="space-y-1">
          <Label>Nur diese Abos rabattieren (optional – Stichwörter, Komma-getrennt)</Label>
          <Input data-testid="ft-qualifying-names" type="text" placeholder="z.B. Standard, Training, Abo"
            value={qualifyingText}
            onChange={(e) => setQualifyingText(e.target.value)} />
          <p className="text-xs text-gray-500">
            Leer = alle aktiven Standard-Abos. Der Rabatt wird pro Abrechnung neu berechnet und
            {isPercent
              ? ` als Prozentsatz (max ${settings.max_total_pct}%) vom jeweiligen Abo-Betrag abgezogen`
              : ` als Franken-Ersparnis (max ${capLabel}) vom Abo-Betrag abgezogen`}
            {' '}(Untergrenze {CHF(settings.min_price)}). Individualpreis-/Rabatt-Kunden bleiben unangetastet.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="friendship-tree-save-btn">
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
        </div>

        {/* Abrechnungs-Anbindung */}
        <div className={`p-3 rounded-lg border ${settings.apply_to_billing ? 'border-green-300 bg-green-50' : 'border-amber-200 bg-amber-50'}`} data-testid="ft-billing-section">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              {settings.apply_to_billing
                ? <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />}
              <div>
                <p className="text-sm font-medium">
                  Auf Abrechnung anwenden
                  <Badge className={`ml-2 text-[10px] ${settings.apply_to_billing ? 'bg-green-600' : 'bg-amber-500'}`}>
                    {settings.apply_to_billing ? 'AKTIV' : 'AUS'}
                  </Badge>
                </p>
                <p className="text-xs text-gray-600">
                  {settings.apply_to_billing
                    ? 'Der Netzwerk-Rabatt senkt echte monatliche Abo-Beträge (pro Abrechnung neu berechnet).'
                    : 'Aktuell reine Anzeige. Beim Scharfschalten senkt der Rabatt echte Abo-Beträge.'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={openBillingPreview} data-testid="ft-billing-preview-btn">
              <Eye className="h-4 w-4 mr-2" /> Trockenlauf-Vorschau
            </Button>
            {settings.apply_to_billing ? (
              <Button variant="outline" size="sm" onClick={() => setBilling(false)} disabled={activating}
                className="border-red-300 text-red-700 hover:bg-red-50" data-testid="ft-billing-deactivate-btn">
                Deaktivieren
              </Button>
            ) : (
              <Button size="sm" onClick={openBillingPreview}
                className="bg-amber-600 hover:bg-amber-700" data-testid="ft-billing-activate-btn">
                Auf Abrechnung anwenden…
              </Button>
            )}
          </div>
        </div>

        {/* Kunden-Vorschau */}
        <div className="pt-4 border-t border-gray-200 space-y-3">
          <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Kunden-Vorschau</Label>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="ft-preview-search" className="pl-9" placeholder="Kunde suchen (Name, E-Mail, Nr.)"
              value={query} onChange={(e) => setQuery(e.target.value)} />
            {results.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto" data-testid="ft-preview-results">
                {results.map((c) => (
                  <button key={c.id}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    onClick={() => loadPreview(c.id, `${c.first_name || ''} ${c.last_name || ''}`.trim())}
                    data-testid={`ft-preview-result-${c.id}`}>
                    {(c.first_name || '') + ' ' + (c.last_name || '')} <span className="text-gray-400">{c.email || ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {loadingPreview && <p className="text-sm text-gray-500">Lädt Vorschau…</p>}
          {preview && !loadingPreview && <FriendshipTreePreview data={preview} />}
        </div>
      </CardContent>
    </Card>

    <Dialog open={billingDialogOpen} onOpenChange={setBillingDialogOpen}>
      <DialogContent className="max-w-2xl" data-testid="ft-billing-dialog">
        <DialogHeader>
          <DialogTitle>Abrechnungswirkung – Trockenlauf</DialogTitle>
          <DialogDescription>
            So würden sich die aktiven Abos mit dem aktuellen Netzwerk-Rabatt ändern.
            Individualpreis-/Rabatt-Kunden bleiben unangetastet.
          </DialogDescription>
        </DialogHeader>
        {loadingBilling && <p className="text-sm text-gray-500 py-6 text-center">Berechne Vorschau…</p>}
        {billingPreview && !loadingBilling && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold" data-testid="ft-billing-changed">{billingPreview.changed_count}</p>
                <p className="text-xs text-gray-600">Abos betroffen</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-green-700" data-testid="ft-billing-total-discount">{CHF(billingPreview.total_monthly_discount)}</p>
                <p className="text-xs text-gray-600">Rabatt / Monat gesamt</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold">{billingPreview.count}</p>
                <p className="text-xs text-gray-600">aktive Abos geprüft</p>
              </div>
            </div>
            <div className="max-h-64 overflow-auto border border-gray-100 rounded-lg divide-y">
              {billingPreview.rows.filter((r) => r.discount > 0 || r.exempt).slice(0, 50).map((r) => (
                <div key={r.subscription_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`ft-billing-row-${r.subscription_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{r.customer_name} <span className="text-gray-400">· {r.subscription_name}</span></p>
                    {r.exempt && <p className="text-[11px] text-amber-700">⚠️ Individueller Preis — kein Rabatt</p>}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    {r.discount > 0 ? (
                      <>
                        <span className="text-gray-400 line-through mr-1">{CHF(r.old_amount)}</span>
                        <span className="font-semibold text-green-700">{CHF(r.new_amount)}</span>
                        <span className="text-[11px] text-green-700 ml-1">(−{CHF(r.discount)})</span>
                      </>
                    ) : (
                      <span className="text-gray-500">{CHF(r.old_amount)}</span>
                    )}
                  </div>
                </div>
              ))}
              {billingPreview.rows.filter((r) => r.discount > 0 || r.exempt).length === 0 && (
                <p className="text-sm text-gray-400 px-3 py-4 text-center">Aktuell würde sich kein Abo ändern (keine aktiven Netzwerke).</p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setBillingDialogOpen(false)} data-testid="ft-billing-cancel-btn">Schliessen</Button>
          {billingPreview && !billingPreview.apply_to_billing && (
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => setBilling(true)} disabled={activating} data-testid="ft-billing-confirm-btn">
              <ShieldCheck className="h-4 w-4 mr-2" />
              {activating ? 'Aktiviere…' : 'Jetzt scharfschalten'}
            </Button>
          )}
          {billingPreview && billingPreview.apply_to_billing && (
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setBilling(false)} disabled={activating} data-testid="ft-billing-confirm-deactivate-btn">
              {activating ? 'Deaktiviere…' : 'Abrechnungswirkung deaktivieren'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

const FriendshipTreePreview = ({ data }) => {
  const isPct = data.discount_mode === 'percent';
  const fmtDisc = (v) => (isPct ? `−${v}%` : `−${CHF(v)}`);
  const capped = data.total_discount > (data.max_total_discount ?? Infinity);
  return (
  <div className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid="ft-preview-panel">
    <div className="flex items-center justify-between">
      <p className="font-semibold text-gray-900">{data.customer_name || 'Kunde'}</p>
      <div className="text-right">
        {isPct ? (
          <>
            <p className="text-2xl font-bold text-green-700" data-testid="ft-preview-price">−{data.discount_pct || 0}%</p>
            <p className="text-xs text-gray-500">
              Beispiel Basis {CHF(data.base_price)} → {CHF(data.effective_price)}{capped ? ' (gedeckelt)' : ''}
            </p>
          </>
        ) : (
          <>
            <p className="text-2xl font-bold text-green-700" data-testid="ft-preview-price">{CHF(data.effective_price)}</p>
            <p className="text-xs text-gray-500">
              Basis {CHF(data.base_price)} · Rabatt −{CHF(data.total_discount_units)}{capped ? ' (gedeckelt)' : ''}
            </p>
          </>
        )}
      </div>
    </div>
    {data.exempt && (
      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800" data-testid="ft-preview-exempt">
        ⚠️ Individueller Preis aktiv — Freundeskreis-Rabatt wird nicht (gestapelt) angewendet.
        {data.individual_price != null && <> Individueller Preis: {CHF(data.individual_price)}.</>}
      </div>
    )}
    <p className="text-sm text-gray-600">
      {data.active_members_total} aktive von {data.members_total} Netzwerk-Mitgliedern (aktiv = trainiert in {data.active_within_days} Tagen)
    </p>
    <div className="space-y-2">
      {(data.generations || []).length === 0 && (
        <p className="text-sm text-gray-400">Noch keine Empfehlungen in diesem Netzwerk.</p>
      )}
      {(data.generations || []).map((g) => (
        <div key={g.generation} className="text-sm" data-testid={`ft-preview-gen-${g.generation}`}>
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-800">
              {g.generation === 1 ? 'Direkte Empfehlungen' : `Generation ${g.generation}`}
              <span className="text-gray-400"> · {g.active_count}/{g.member_count} aktiv</span>
            </span>
            <span className="text-green-700 font-medium">{fmtDisc(g.discount)}</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {g.members.map((m) => (
              <span key={m.id}
                className={`text-xs px-2 py-0.5 rounded-full ${m.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                {m.name || '•••'}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};
